import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Users, Award, Clock } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Transparency',
      description: 'No hidden fees, no surprises. What you see is what you pay.',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'We build lasting relationships through exceptional service.',
      color: 'bg-emerald-100 text-emerald-600'
    },
    {
      icon: Award,
      title: 'Quality',
      description: 'Only well-maintained, reliable vehicles in our fleet.',
      color: 'bg-amber-100 text-amber-600'
    },
    {
      icon: Clock,
      title: 'Reliability',
      description: '24/7 support and roadside assistance for peace of mind.',
      color: 'bg-purple-100 text-purple-600'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                About Fair Rideshare LLC
              </h1>
              <p className="text-lg text-slate-500 mb-6">
                Founded with a mission to make vehicle rentals simple, transparent, and 
                affordable for rideshare drivers and professionals. We believe everyone 
                deserves access to reliable transportation without the complexity.
              </p>
              <p className="text-slate-500">
                Our team brings together decades of experience in automotive and customer 
                service industries. We've built Fair Rideshare to be the company we wished 
                existed when we needed affordable vehicle rentals.
              </p>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1727893512947-8bdc773ceb02?w=600"
                alt="Handing car keys"
                className="rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              These principles guide everything we do at Fair Rideshare
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="bg-white border-slate-100 card-hover" data-testid={`value-card-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className={`feature-icon ${value.color} mx-auto mb-4`}>
                    <value.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{value.title}</h3>
                  <p className="text-slate-500 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-4 bg-slate-900" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div>
              <span className="block text-4xl font-bold text-white mb-2">500+</span>
              <span className="text-slate-400">Happy Customers</span>
            </div>
            <div>
              <span className="block text-4xl font-bold text-white mb-2">50+</span>
              <span className="text-slate-400">Vehicles in Fleet</span>
            </div>
            <div>
              <span className="block text-4xl font-bold text-white mb-2">24/7</span>
              <span className="text-slate-400">Customer Support</span>
            </div>
            <div>
              <span className="block text-4xl font-bold text-white mb-2">5+</span>
              <span className="text-slate-400">Years Experience</span>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 px-4 bg-white" data-testid="mission-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Our Mission
          </h2>
          <p className="text-xl text-slate-500 leading-relaxed">
            "To provide transparent, affordable, and reliable vehicle rentals that empower 
            rideshare drivers and professionals to achieve their goals. We're committed to 
            building trust through honest pricing, quality vehicles, and exceptional customer 
            service."
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
